/**
 * Shared parallel-upload-Reducer fuer Multi-File-Modals.
 *
 * Capabilities:
 *  - addFiles: aus FileList neue Rows ableiten und dedupen
 *  - patchRow: ein Feld einer Row aktualisieren
 *  - removeRow: Row vor dem Submit entfernen
 *  - submit: alle Rows parallel hochladen, Result aggregieren, Toast feuern
 *
 * Reichweite ist bewusst klein — Modal entscheidet UI, Hook macht State + Upload-Lifecycle.
 */
import { useState } from 'react';
import { toast } from 'sonner';

type Status = 'idle' | 'uploading' | 'done' | 'error';

interface Row {
  id: string;
  status: Status;
  error: string | null;
}

interface Config<R extends Row, Meta> {
  buildRow: (file: File) => R;
  isValid: (row: R) => boolean;
  toMeta: (row: R) => Meta;
  upload: (file: File, meta: Meta) => Promise<void>;
  invalidMessage: string;
  noneMessage: string;
  partialMessage: (success: number, total: number) => string;
  successMessage: (count: number) => string;
  onAllDone: () => void;
}

export function useMultiFileUpload<R extends Row & { file: File }, Meta>(cfg: Config<R, Meta>) {
  const [rows, setRows] = useState<R[]>([]);
  const [uploading, setUploading] = useState(false);

  function reset() {
    setRows([]);
    setUploading(false);
  }

  function addFilesArray(files: File[]) {
    if (files.length === 0) return;
    const next = files.map(cfg.buildRow);
    setRows((prev) => [...prev, ...next.filter((n) => !prev.some((p) => p.id === n.id))]);
  }

  function addFiles(files: FileList | null) {
    if (!files) return;
    addFilesArray(Array.from(files));
  }

  function patchRow(id: string, patch: Partial<R>) {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  function removeRow(id: string) {
    setRows((prev) => prev.filter((r) => r.id !== id));
  }

  async function submit() {
    // Bereits erfolgreiche Rows NICHT erneut hochladen (Retry nach Teil-Fehler
    // wuerde sonst Rechnungen/Vertraege doppelt anlegen).
    const pending = rows.filter((r) => r.status !== 'done');
    if (pending.some((r) => !cfg.isValid(r))) {
      toast.error(cfg.invalidMessage);
      return;
    }
    if (pending.length === 0) {
      cfg.onAllDone();
      reset();
      return;
    }
    const pendingIds = new Set(pending.map((r) => r.id));
    setUploading(true);
    setRows((prev) =>
      prev.map((r) =>
        pendingIds.has(r.id) ? { ...r, status: 'uploading' as Status, error: null } : r,
      ),
    );
    const results = await Promise.allSettled(pending.map((r) => cfg.upload(r.file, cfg.toMeta(r))));
    const successCount = results.filter((res) => res.status === 'fulfilled').length;
    setRows((prev) =>
      prev.map((r) => {
        const idx = pending.findIndex((p) => p.id === r.id);
        if (idx === -1) return r;
        const res = results[idx];
        if (res && res.status === 'fulfilled') {
          return { ...r, status: 'done' as Status, error: null };
        }
        const msg =
          res && res.status === 'rejected' && res.reason instanceof Error
            ? res.reason.message
            : 'Fehler';
        return { ...r, status: 'error' as Status, error: msg };
      }),
    );
    setUploading(false);
    if (successCount === pending.length) {
      toast.success(cfg.successMessage(rows.length));
      cfg.onAllDone();
      reset();
    } else if (successCount > 0) {
      toast.warning(cfg.partialMessage(successCount, pending.length));
    } else {
      toast.error(cfg.noneMessage);
    }
  }

  const canSubmit = rows.some((r) => r.status !== 'done') && !uploading && rows.every(cfg.isValid);

  return {
    rows,
    uploading,
    canSubmit,
    addFiles,
    addFilesArray,
    patchRow,
    removeRow,
    submit,
    reset,
  };
}
